// Long-form insight articles — written for SEO + AEO citation potential.
// Each article is shaped to be (a) plainly answerable from the intro paragraph,
// (b) substantive enough to rank organically, (c) attributed to Richard L. Bufkin.

export const INSIGHTS = [
  {
    slug: "lost-profits-damages-methodology",
    title: "How a CFF Attorney Approaches Lost-Profits Damages",
    excerpt:
      "Lost-profits cases are won and lost on three questions the jury actually wants answered. Here's how the methodology should be built so it survives cross-examination.",
    category: "Forensic Accounting",
    readTime: "7 min read",
    datePublished: "2026-03-12",
    dateModified: "2026-03-12",
    keywords:
      "lost profits damages, economic damages methodology, expert witness lost profits, AICPA CFF, Texas business litigation",
    body: [
      {
        type: "p",
        text: "Lost-profits litigation is one of the most heavily challenged areas of expert testimony. Most damages opinions get attacked on the same three fronts — and most fail on at least one of them. After fifty years trying these cases as both counsel and as a testifying expert under the AICPA's Certified in Financial Forensics standards, the pattern is consistent enough to teach.",
      },
      {
        type: "h2",
        text: "1. Is the methodology grounded in evidence, or in assumption?",
      },
      {
        type: "p",
        text: "The 'before-and-after' method and the 'yardstick' method are the workhorses. Both fail the same way: when the expert can't show the historical record (before) or comparable benchmark (yardstick) is genuinely comparable to what would have happened absent the wrongful act. The fix is rigorous: every comparative period or peer benchmark must be defended with the same evidentiary discipline as a contested business fact.",
      },
      {
        type: "p",
        text: "Forecasts based on management projections invite cross. The defensible alternative is forecasting based on the actual operating history of the plaintiff, supplemented by industry data — not on what management told the expert the company would do.",
      },
      {
        type: "h2",
        text: "2. Are fixed costs handled correctly?",
      },
      {
        type: "p",
        text: "The most common methodological error in lost-profits work is treating the plaintiff's fixed costs as if they were variable. Lost profits is contribution margin times lost units — not gross margin, and certainly not revenue. An expert who casually backs out 'cost of goods sold' without separating variable from fixed has overstated damages.",
      },
      {
        type: "p",
        text: "On cross, this is fatal. The defense expert will walk the jury through the income statement line by line, and the inflated damage number collapses. A clean CFF-grade analysis isolates the contribution margin explicitly and lets the jury see the work.",
      },
      {
        type: "h2",
        text: "3. Was the loss reasonably foreseeable and reasonably mitigated?",
      },
      {
        type: "p",
        text: "Texas law requires causation, foreseeability, and reasonable certainty. The financial work must connect the wrongful act to the lost contribution margin in a way the jury can follow. If the plaintiff failed to mitigate — for example, didn't pursue a viable replacement customer — the damages number must be adjusted, not buried.",
      },
      {
        type: "h2",
        text: "Why this matters for trial counsel",
      },
      {
        type: "p",
        text: "Engaging an expert who is also a trial lawyer changes the rhythm of preparation. The damages model gets built with the cross-examination already in mind. Demonstrative exhibits are designed before the report is finalized, not after. And the report itself reads like trial-ready testimony — because that is how it will be used.",
      },
      {
        type: "p",
        text: "If a lost-profits case is at the center of your matter, the methodology has to be defensible before it is persuasive. The order matters.",
      },
    ],
  },
  {
    slug: "fraudulent-transfer-patterns-texas-bankruptcy",
    title: "The 4 Most Common Fraudulent-Transfer Patterns in Texas Bankruptcy",
    excerpt:
      "Bankruptcy trustees see the same four fact patterns over and over. Here's what they look like in practice — and why each one collapses under forensic scrutiny.",
    category: "Bankruptcy",
    readTime: "8 min read",
    datePublished: "2026-02-04",
    dateModified: "2026-02-04",
    keywords:
      "fraudulent transfer Texas, bankruptcy trustee, avoidance action, badges of fraud, TUFTA, breach of fiduciary duty",
    body: [
      {
        type: "p",
        text: "Trustees pursuing fraudulent-transfer claims under the Texas Uniform Fraudulent Transfer Act (TUFTA) or 11 U.S.C. § 548 see the same fact patterns recur with striking consistency. Recognizing them quickly compresses investigation time and sharpens the badges-of-fraud analysis.",
      },
      {
        type: "h2",
        text: "Pattern 1 — The Family-Member Transfer",
      },
      {
        type: "p",
        text: "A debtor transfers a valuable asset to a spouse, sibling, or adult child weeks or months before the bankruptcy filing. The transfer is documented but priced at a fraction of fair market value. Sometimes the recited consideration is 'love and affection' or 'past services'. Forensic review tracks bank records to confirm no payment changed hands — or, if it did, where the money came back from.",
      },
      {
        type: "h2",
        text: "Pattern 2 — The Insider-Entity Transfer",
      },
      {
        type: "p",
        text: "An operating company transfers receivables, inventory, equipment, or goodwill to a newly-formed entity owned by the same principals — often called a 'phoenix' transaction. The new entity continues the same business with the same customers and employees at a different address. Forensic work focuses on tracing customer continuity, payroll continuity, and the absence of arm's-length valuation.",
      },
      {
        type: "h2",
        text: "Pattern 3 — The Pre-Filing Distribution",
      },
      {
        type: "p",
        text: "Owners take a large compensation increase, dividend, or 'loan repayment' in the months before filing. The defense will say the payment matched a legitimate accrued obligation. Forensic accounting tests this against historical compensation patterns, prior loan terms, and contemporaneous board minutes — most of which don't exist.",
      },
      {
        type: "h2",
        text: "Pattern 4 — The Round-Trip Transaction",
      },
      {
        type: "p",
        text: "Cash leaves the debtor, takes a documented business detour through one or more affiliates, and lands with an insider. Each step has a plausible business explanation. The fraud is visible only when the full chain is mapped — which is where a CPA-attorney's work product is most valuable.",
      },
      {
        type: "h2",
        text: "What trustees should ask first",
      },
      {
        type: "p",
        text: "Before any motion is filed, three questions resolve direction: (1) Was the debtor insolvent at or as a result of the transfer? (2) Did the debtor receive reasonably equivalent value? (3) Are there badges of fraud — insider relationships, concealment, retained control, suspicious timing? An honest answer to those three frames the entire prosecution.",
      },
      {
        type: "p",
        text: "Recovery actions are won on documentation discipline more than on argument. Get the records, build the trace, and let the numbers tell the story.",
      },
    ],
  },
  {
    slug: "consulting-vs-testifying-expert",
    title: "When to Bring in a Consulting Expert vs. a Testifying Expert",
    excerpt:
      "Two different roles, two different rule sets, and two very different ways to use a forensic accountant. Getting this decision right early can change the case.",
    category: "Expert Witness",
    readTime: "5 min read",
    datePublished: "2026-01-22",
    dateModified: "2026-01-22",
    keywords:
      "consulting expert vs testifying expert, attorney work product, discovery protection, forensic accounting expert, Rule 26",
    body: [
      {
        type: "p",
        text: "Trial lawyers regularly miss an opportunity by engaging an expert in the wrong role at the wrong time. The choice between consulting and testifying engagement is not just procedural — it controls what is discoverable, what is protected, and what role the expert can play in case strategy.",
      },
      {
        type: "h2",
        text: "Consulting expert",
      },
      {
        type: "p",
        text: "A consulting expert advises counsel privately. Their work product is generally protected from discovery in most jurisdictions, subject to limited exceptions. The role is strategic: stress-test theories, evaluate the strength of the opposing damages model, brief counsel on the financial story, design the deposition outline for the opposing expert.",
      },
      {
        type: "p",
        text: "Use a consulting expert when the team needs deep financial guidance without yet committing to the opinions in writing. Engage early — ideally before discovery is fully scoped — so the expert can shape the document requests.",
      },
      {
        type: "h2",
        text: "Testifying expert",
      },
      {
        type: "p",
        text: "A testifying expert produces a report, sits for deposition, and may take the stand. Everything they consider, every draft, every communication with counsel can become discoverable depending on the rules in play. Their independence — and the documentary record supporting it — is what gives the opinion its weight.",
      },
      {
        type: "p",
        text: "Use a testifying expert when an opinion has to be defended in front of the trier of fact. Engage them once the methodology and the underlying facts are stable enough to commit to.",
      },
      {
        type: "h2",
        text: "The hybrid path",
      },
      {
        type: "p",
        text: "On significant financial cases, the same forensic accountant can sometimes serve in both roles sequentially — consulting first, then designated testifying later. The transition needs to be handled carefully so privilege is not waived. Done correctly, the trial team gets the strategic value of a consulting expert and the courtroom weight of a testifying expert in one engagement.",
      },
      {
        type: "h2",
        text: "When the expert is also a trial lawyer",
      },
      {
        type: "p",
        text: "An expert who has tried cases brings something specific to the testifying role: an ear for how opinions sound on cross. They write reports that survive Daubert challenges because they have made those challenges themselves. They build exhibits that work for juries because they have stood next to juries.",
      },
      {
        type: "p",
        text: "Pick the role deliberately. The economics — and the outcome — depend on it.",
      },
    ],
  },
  {
    slug: "marital-property-agreement-texas-checklist",
    title: "Reading a Texas Marital Property Agreement Before You Sign It",
    excerpt:
      "Most pre- and post-nuptial agreements look fine on the kitchen table and fall apart in court. Here's the financial-side checklist that should run before the ink dries.",
    category: "Family Law",
    readTime: "6 min read",
    datePublished: "2026-04-08",
    dateModified: "2026-04-08",
    keywords:
      "Texas marital property agreement, prenuptial agreement Texas, partition and exchange agreement, community property characterization, marital agreement enforceability",
    body: [
      {
        type: "p",
        text: "Texas is a community property state with statutory tools — partition and exchange agreements, pre-marital agreements, post-marital agreements — that let spouses re-characterize what would otherwise be community property as separate property. Used well, they prevent litigation. Used badly, they create it. The financial side of the document is where most of the failures hide.",
      },
      {
        type: "h2",
        text: "1. Was there meaningful financial disclosure?",
      },
      {
        type: "p",
        text: "Texas Family Code requires that each party either receive fair and reasonable disclosure of the other's property and financial obligations or expressly, voluntarily, and in writing waive any right to that disclosure. The disclosure cannot be hand-waved. Schedules of assets and liabilities should be attached. Each item should have a date, a description, and a valuation. Round numbers and 'approximately' are red flags.",
      },
      {
        type: "h2",
        text: "2. Are characterization rules actually applied — not just stated?",
      },
      {
        type: "p",
        text: "Agreements often recite the right characterization rules but fail to track the assets through them. Earnings on separate property remain community in Texas unless a written agreement says otherwise. Re-titling separate-property funds into a joint account creates a presumption of gift to the community. The agreement has to anticipate these mechanics — and the parties' day-to-day banking habits have to match.",
      },
      {
        type: "h2",
        text: "3. Is there a tracing methodology built in?",
      },
      {
        type: "p",
        text: "Separate property frequently gets commingled with community funds. Without a written tracing methodology agreed to in advance, the burden of proof at divorce or probate falls on the spouse claiming separate character — by clear and convincing evidence. A well-drafted agreement specifies the tracing method (community-out-first, minimum-balance, pro-rata) so future litigation is shorter and cheaper.",
      },
      {
        type: "h2",
        text: "4. Was the agreement signed without duress or unconscionability?",
      },
      {
        type: "p",
        text: "Texas enforces premarital agreements unless the party against whom enforcement is sought proves the agreement was either not voluntary or was unconscionable when signed AND that party did not have fair disclosure and did not waive disclosure. The voluntariness question turns on timing, independent counsel, capacity, and pressure. The unconscionability question turns on the substance.",
      },
      {
        type: "h2",
        text: "Why a CPA's eye matters here",
      },
      {
        type: "p",
        text: "The drafting lawyer focuses on the statutory boxes. The forensic accountant runs the document against the actual balance sheet — the entities the spouses own, the income streams, the debt obligations, the trust interests. Issues that look minor on the kitchen table are usually the ones that matter in court ten years later: stock options vesting on a schedule, retirement-plan beneficiary designations, deferred compensation, contingent earnouts, intercompany loans. None of those care about the recitals at the front of the document.",
      },
      {
        type: "p",
        text: "Sign the agreement only after a CPA-attorney has run the numbers against the language. That is the cheap insurance.",
      },
    ],
  },
  {
    slug: "construction-warranty-disputes-forensic-accounting",
    title: "Why Construction Warranty Disputes End Up in Forensic Accounting",
    excerpt:
      "A construction dispute that starts as a builder–owner argument over scope almost always becomes a numbers case. Here's what trial lawyers should anticipate.",
    category: "Business Litigation",
    readTime: "6 min read",
    datePublished: "2026-05-06",
    dateModified: "2026-05-06",
    keywords:
      "construction warranty dispute, owner builder litigation, change order analysis, cost-to-complete, construction damages Texas",
    body: [
      {
        type: "p",
        text: "Recent matters at the firm have included a meaningful number of breached construction contracts and warranty disputes between owners, builders, and contractors. The dispute usually starts as a quality argument — what was promised, what was delivered. By the time it reaches trial, it is almost entirely a financial case. Knowing why helps trial lawyers prepare earlier.",
      },
      {
        type: "h2",
        text: "The dispute migrates from scope to cost",
      },
      {
        type: "p",
        text: "Most contracts allocate risk by reference to a scope of work, change-order procedures, and warranty terms. When a dispute arises, the parties first argue about scope. But the trier of fact ultimately has to award damages — and damages are computed from costs. Cost to complete, cost to repair, cost overruns, lost profit on extra work, retention withheld, draw schedule deviations, and pre-judgment interest all become the actual battleground.",
      },
      {
        type: "h2",
        text: "Where the numbers fail under examination",
      },
      {
        type: "p",
        text: "Three financial failures show up repeatedly. First, change orders that were performed but never approved in writing — work was done, costs were incurred, no documented authorization exists. Second, retention and progress payments that don't reconcile to a coherent schedule of values. Third, cost-to-complete estimates submitted at trial that cannot be traced back to the actual subcontractor bids or material quotes that supposedly support them.",
      },
      {
        type: "h2",
        text: "What rigorous forensic work produces",
      },
      {
        type: "p",
        text: "A forensic accountant approaches the file the way an auditor approaches a year-end close. Every dollar in the damages model traces to a source document. Every change order ties to the authorization (or to its absence). Every cost-to-complete number ties to a vendor quote or to a defensible industry benchmark. Demonstratives are built so the jury can follow one transaction from contract clause to general ledger to bank statement.",
      },
      {
        type: "h2",
        text: "Anticipating the defense",
      },
      {
        type: "p",
        text: "Defense counsel will almost always attempt three moves: (a) attack the qualifications of any non-CPA financial witness on construction-cost matters; (b) introduce a betterment argument — that any repair award would put the owner in a better position than if performance had been timely; and (c) impeach the cost-to-complete with prior inconsistent estimates from the owner's own files. Each is defeated by documentary discipline, not by argument.",
      },
      {
        type: "h2",
        text: "The single highest-leverage step",
      },
      {
        type: "p",
        text: "Hire the forensic accountant before discovery is scoped. The document requests that surface change-order authorizations, draw-request packages, and contemporaneous cost reports are not the same requests a generalist litigator drafts. Get the right documents in the door and the case shortens — and the settlement number moves.",
      },
    ],
  },
  {
    slug: "asymmetric-information-defendant-owns-the-books",
    title: "Quantifying Damages When the Defendant Owns the Books",
    excerpt:
      "In fiduciary, partnership, and minority-shareholder cases the financial records sit with the very party you're suing. Here's the methodology that survives that asymmetry.",
    category: "Forensic Accounting",
    readTime: "7 min read",
    datePublished: "2026-06-01",
    dateModified: "2026-06-01",
    keywords:
      "minority shareholder oppression, partnership disputes damages, fiduciary breach damages, adverse inference, asymmetric financial information",
    body: [
      {
        type: "p",
        text: "Some cases come with full access to the financial records. Many do not. In partnership disputes, minority-shareholder oppression claims, and fiduciary-breach actions, the books are typically in the hands of the very party against whom relief is sought. That asymmetry is not just an inconvenience — it shapes the entire damages methodology.",
      },
      {
        type: "h2",
        text: "Start with what is undisputed",
      },
      {
        type: "p",
        text: "Even in adversarial situations, certain documents are difficult for the defendant to recreate or suppress: filed tax returns, audited financial statements, third-party bank records, vendor and customer confirmations, public-record filings. The first phase of the engagement is mapping every undisputed external touchpoint that constrains the universe of possible numbers. That perimeter, by itself, often gets you within a defensible range.",
      },
      {
        type: "h2",
        text: "Use proper discovery — and use it forensically",
      },
      {
        type: "p",
        text: "Generic financial requests produce generic financial productions. Forensic-grade discovery asks for the underlying journals, sub-ledgers, payroll registers, and bank-statement detail — not just summaries. It asks for items by transaction code, by counterparty, by account. It asks for system audit logs. The right requests, drafted with the forensic accountant in the room, change what arrives.",
      },
      {
        type: "h2",
        text: "Apply benchmarks and reasonableness tests",
      },
      {
        type: "p",
        text: "When records are still incomplete, industry benchmarks and internal reasonableness tests fill the gap. Margins that would be implausible for a comparable business are flagged. Compensation that exceeds industry norms is identified. Cash flows that don't reconcile to operating activity are surfaced. Each gap becomes either a fact for the trier to weigh or grounds for an adverse-inference instruction.",
      },
      {
        type: "h2",
        text: "Pursue the adverse inference",
      },
      {
        type: "p",
        text: "Texas law and federal procedure both recognize the right to an adverse inference when a party fails to produce records within its exclusive control. A forensic accountant who has methodically documented every gap, every request, and every non-response gives trial counsel the record needed to obtain that instruction. It is one of the most powerful financial outcomes available — and it is built case-by-case from the moment the engagement starts.",
      },
      {
        type: "h2",
        text: "Why all of this matters at settlement",
      },
      {
        type: "p",
        text: "Settlement value moves when the defendant realizes its information advantage is shrinking. A damages model that is conservative, sourced to undisputed external records, and supplemented by a defensible adverse-inference argument is uncomfortable for the other side to litigate against. The case settles — or it settles for more.",
      },
      {
        type: "p",
        text: "Asymmetric information is the problem statement. Methodology is the answer.",
      },
    ],
  },
];
